<template>
  <div class="vehicle-model-list-wrapper">
    <Select
      class="vehicle-model-list__select"
      :placeholder="placeholder"
      :value="selectedValue"
      type="autocomplete"
      :options="localOptions"
      :is-disabled="isDisabled"
      :is-loading="isLoading"
      :empty-text="emptyText"
      value-key="id"
      label-key="name"
      @search="onSearch"
      @input="onInput"
      @change="onChange"
    >
      <template #option="{ option }">
        <p v-if="option" class="vehicle-model-list-option__info-text">
          {{ option.name }}
        </p>
      </template>
    </Select>
  </div>
</template>

<script>
import { Select } from '@/shared/ui/form';

export default {
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [String, Number, Object],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every(item =>
          item && typeof item === 'object' &&
          'id' in item && 'name' in item
        );
      },
    },
    placeholder: {
      type: String,
      default: 'Виберіть модель транспортного засобу',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Спочатку виберіть марку',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      localOptions: [],
    };
  },
  components: { Select },
  computed: {
    hasOptions() {
      return this.localOptions && this.localOptions.length > 0;
    },
    selectedValue() {
      if (!this.value) return null;
      if (typeof this.value === 'object') return this.value.id;
      const found = this.findOptionById(this.value);
      return found ? found.id : this.value;
    },
    filteredOptions() {
      if (!this.hasOptions) return [];

      if (!this.searchQuery) {
        return this.localOptions;
      }

      const lower = this.searchQuery.toLowerCase();
      return this.localOptions.filter(m =>
        m.name.toLowerCase().includes(lower)
      );
    },
  },
  methods: {
    findOptionById(id) {
      return this.localOptions.find(m => m.id === id || m.name === id);
    },
    ensureOptionInList(option) {
      if (!option || !option.id || !option.name) return;

      const exists = this.localOptions.some(
        m => m.id === option.id || m.name === option.name
      );

      if (!exists) {
        this.localOptions = [...this.localOptions, option];
      }
    },
    onSearch(query) {
      this.searchQuery = query || '';
    },
    onInput(value) {
      const selectedOption = this.findOptionById(value);
      const result = selectedOption || null;
      this.$emit('input', result);
    },
    onChange(value) {
      const selectedOption = this.findOptionById(value);
      const result = selectedOption || null;
      this.$emit('change', result);
    },
    reset() {
      this.searchQuery = '';
      this.$emit('input', null);
      this.$emit('change', null);
    },
    selectSingleOption() {
      if (this.localOptions.length === 1) {
        const singleOption = this.localOptions[0];
        const currentId = this.value
          ? (typeof this.value === 'object' ? this.value.id : this.value)
          : null;

        if (currentId !== singleOption.id) {
          this.$emit('input', singleOption);
          this.$emit('change', singleOption);
        }
      }
    },
  },
  watch: {
    options: {
      immediate: true,
      handler(newOptions) {
        this.searchQuery = '';
        this.localOptions = [...(newOptions || [])];

        if (this.value) {
          const option = typeof this.value === 'object'
            ? this.value
            : { id: this.value, name: this.value };
          this.ensureOptionInList(option);
        }

        if (newOptions && newOptions.length === 1) {
          this.selectSingleOption();
        }
      },
    },
    value: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.searchQuery = '';
          return;
        }

        const option = typeof newValue === 'object'
          ? newValue
          : { id: newValue, name: newValue };

        this.ensureOptionInList(option);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.vehicle-model-list-wrapper {
  position: relative;
  width: 100%;
}

.vehicle-model-list__select {
  width: 100%;
}

.vehicle-model-list-option__info-text {
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  strong {
    font-weight: 600;
  }
}
</style>
